import React, { useCallback, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { motion } from 'framer-motion'
import { Tag, Database, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Bank, DocumentClass, DocumentClassSource } from '../../../api/apiTypes'
import { fetchDocumentSourcesByClass, updateBankAliases } from '../../../api/functionApi'
import AliasModal from './AliasModal'
import DocumentSourcesModal from './DocumentSourcesModal'
import { labelMapping } from '../../../utils/documentLabels'

interface ContainerDettaglioBanksProps {
  bank: Bank
  documents: DocumentClass[]
}

const toast = (title: string) =>
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title,
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
  })

const ContainerDettaglioBanks: React.FC<ContainerDettaglioBanksProps> = ({ bank, documents }) => {
  const [openAliasModal, setOpenAliasModal] = useState(false)
  const [openDocsModal, setOpenDocsModal] = useState<string | null>(null)
  const [aliases, setAliases] = useState<string[]>(bank.aliases || [])
  const [fontiCounts, setFontiCounts] = useState<Record<string, number>>({})
  const [docSources, setDocSources] = useState<Record<string, DocumentClassSource[]>>({})

  const loadSources = useCallback(async () => {
    const counts: Record<string, number> = {}
    const sourcesMap: Record<string, DocumentClassSource[]> = {}

    for (const doc of documents) {
      try {
        const sources = await fetchDocumentSourcesByClass(doc.id)
        counts[doc.document_type] = sources.filter(src => src.is_active).length
        sourcesMap[doc.document_type] = sources
      } catch (error) {
        console.error(`Errore caricando fonti per ${doc.document_type}`, error)
        counts[doc.document_type] = 0
        sourcesMap[doc.document_type] = []
      }
    }

    setFontiCounts(counts)
    setDocSources(sourcesMap)
  }, [documents])

  useEffect(() => {
    loadSources()
  }, [loadSources])

  /**
   * L'endpoint riscrive l'intera lista alias, non il singolo elemento: si
   * rilancia l'errore cosi' il modale lo mostra accanto al campo e lo stato
   * locale non diverge da quello del server.
   */
  const handleAddAlias = async (nuovo: string) => {
    const updated = [...aliases, nuovo]
    await updateBankAliases(bank.id, updated)
    setAliases(updated)
    toast('Alias aggiunto')
  }

  const handleRemoveAlias = async (aliasToRemove: string) => {
    const updated = aliases.filter(a => a !== aliasToRemove)
    await updateBankAliases(bank.id, updated)
    setAliases(updated)
    toast(`Alias «${aliasToRemove}» rimosso`)
  }

  const scoperte = documents.filter(
    d => d.is_required && (fontiCounts[d.document_type] ?? 0) === 0,
  ).length

  return (
    <div className="flex flex-col gap-10">
      {/* ALIAS */}
      <section>
        <h2 className="text-xl font-semibold font-heading text-textPrimary mb-1">Alias</h2>
        <p className="text-textSecondary text-sm font-paragraph mb-4">
          Nomi alternativi con cui questa banca compare nei documenti.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setOpenAliasModal(true)}
            className="group text-left p-4 rounded-xl border border-borderDivider bg-white transition-all hover:shadow-md hover:border-textPrimary/20"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                <Tag className="w-4 h-4 text-textPrimary/50" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-textPrimary leading-snug">Alias</h3>
                  <ChevronRight className="w-4 h-4 text-textMuted shrink-0 group-hover:text-textPrimary transition-colors" />
                </div>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-textSecondary border border-borderDivider mt-1.5">
                  {aliases.length} {aliases.length === 1 ? 'alias' : 'alias'}
                </span>
                <p className="text-[11px] text-textMuted leading-relaxed mt-2">
                  Configura gli alias per questo intermediario.
                </p>
              </div>
            </div>
          </motion.button>
        </div>
      </section>

      {/* FONTI */}
      <section>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold font-heading text-textPrimary mb-1">Fonti</h2>
            <p className="text-textSecondary text-sm font-paragraph">
              Seleziona una classe documento per gestirne le fonti.
            </p>
          </div>

          {documents.length > 0 && (
            <span
              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium ${
                scoperte > 0
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-green-50 text-green-700 border-green-200'
              }`}
            >
              {scoperte > 0 ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {scoperte}{' '}
                  {scoperte === 1 ? 'classe obbligatoria scoperta' : 'classi obbligatorie scoperte'}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Tutte le classi obbligatorie hanno una fonte
                </>
              )}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {documents.map((doc, idx) => {
            const mapping = labelMapping[doc.document_type] || {
              label: doc.document_type
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
              description: doc.description || '',
            }

            const count = docSources[doc.document_type]?.filter(d => d.is_active).length ?? 0
            const scoperta = doc.is_required && count === 0

            return (
              <motion.button
                key={doc.document_type}
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                onClick={() => setOpenDocsModal(doc.document_type)}
                className={`group text-left p-4 rounded-xl border bg-white transition-all hover:shadow-md ${
                  scoperta
                    ? 'border-amber-200 bg-amber-50/30 hover:border-amber-300'
                    : 'border-borderDivider hover:border-textPrimary/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      scoperta ? 'bg-amber-100' : count > 0 ? 'bg-green-100' : 'bg-gray-50'
                    }`}
                  >
                    {scoperta ? (
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    ) : count > 0 ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Database className="w-4 h-4 text-textPrimary/50" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-semibold text-textPrimary leading-snug">
                        {mapping.label}
                      </h3>
                      <ChevronRight className="w-4 h-4 text-textMuted shrink-0 group-hover:text-textPrimary transition-colors" />
                    </div>

                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                          scoperta
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-gray-50 text-textSecondary border-borderDivider'
                        }`}
                      >
                        {count} {count === 1 ? 'fonte' : 'fonti'}
                      </span>
                      {doc.is_required && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-500 border border-violet-100">
                          obbligatoria
                        </span>
                      )}
                    </div>

                    <p className="text-[11px] text-textMuted leading-relaxed mt-2">
                      {mapping.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            )
          })}
        </div>
      </section>

      {openDocsModal && (
        <DocumentSourcesModal
          openDocsModal={openDocsModal}
          setOpenDocsModal={setOpenDocsModal}
          documents={documents}
          docSources={docSources}
          setDocSources={setDocSources}
          fontiCounts={fontiCounts}
          setFontiCounts={setFontiCounts}
          reloadSources={loadSources}
          bankId={bank.id}
          bankName={bank.name}
        />
      )}

      {openAliasModal && (
        <AliasModal
          aliases={aliases}
          onAddAlias={handleAddAlias}
          onRemoveAlias={handleRemoveAlias}
          onClose={() => setOpenAliasModal(false)}
          bankName={bank.name}
        />
      )}
    </div>
  )
}

export default ContainerDettaglioBanks
